import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { captureInstallPrompt, promptInstall, isAppInstalled } from '@/utils/pwaUtils';

function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstallable, setIsInstallable] = useState(false);
  
  // Detect iOS on initial render (not in effect)
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  useEffect(() => {
    // Don't show if already installed
    if (isAppInstalled()) {
      return;
    }

    // Check if dismissed recently (within 7 days)
    const dismissedAt = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        return;
      }
    }

    // Listen for install prompt event (Chrome/Edge/etc)
    const handleBeforeInstallPrompt = (e) => {
      captureInstallPrompt(e);
      setIsInstallable(true);
      // Show prompt after 30 seconds on site
      setTimeout(() => setShowPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // For iOS, show instructions after 30 seconds
    if (isIOS) {
      const timer = setTimeout(() => setShowPrompt(true), 30000);
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [isIOS]);

  const handleInstall = async () => {
    if (isIOS) {
      // Can't programmatically install on iOS, just keep showing instructions
      return;
    }
    
    const result = await promptInstall();
    if (result.outcome === 'accepted') {
      setShowPrompt(false);
      setIsInstallable(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
  };

  if (!showPrompt || (!isInstallable && !isIOS)) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-gradient-to-r from-[#050505] to-[#0a0a0a] border border-[#00F0FF]/30 rounded-xl p-4 shadow-2xl shadow-[#00F0FF]/10">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-neutral-400 hover:text-white p-1"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2 bg-[#00F0FF]/10 rounded-lg">
            <Smartphone className="w-6 h-6 text-[#00F0FF]" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-white mb-1">Install AI Ledger App</h3>
            {isIOS ? (
              <p className="text-sm text-neutral-400 mb-3">
                Tap <span className="text-[#00F0FF]">Share</span> then <span className="text-[#00F0FF]">&quot;Add to Home Screen&quot;</span> for the best experience.
              </p>
            ) : (
              <p className="text-sm text-neutral-400 mb-3">
                Get quick access to AI products right from your home screen.
              </p>
            )}
            {!isIOS && (
              <Button
                onClick={handleInstall}
                size="sm"
                className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black font-medium"
              >
                <Download className="w-4 h-4 mr-2" />
                Install App
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
